import type {
  ContentRef,
  MetadataRef,
  ResourceContext,
  SpatialNode,
  TileRefinement,
  TilesetDocument,
} from '@/plugins/tiles/contracts'
import { parseTilesBoundingVolume } from '@/plugins/tiles/spatial/tilesSpatial'
import { TilesError } from '@/plugins/tiles/TilesError'

const IDENTITY = Object.freeze([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1])

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export interface TilesetParseOptions {
  readonly idPrefix?: string
  readonly maxDepth?: number
  readonly byteLength?: number
}

/** M0 的稳定 1.1 结构读取器；不接收任何第三方运行时对象。 */
export class TilesetJsonParser {
  parse(
    value: unknown,
    context: ResourceContext,
    options: TilesetParseOptions = {}
  ): TilesetDocument {
    const warnings: string[] = []
    const sourceUrl = context.documentUrl
    const fail = (message: string, path = 'root', extension?: string): never => {
      throw new TilesError({
        code: extension ? 'unsupported-capability' : 'invalid-input',
        stage: 'hierarchy',
        sourceUrl,
        tileId: `${options.idPrefix ?? sourceUrl}#${path}`,
        extension,
        message,
      })
    }
    if (!isRecord(value) || !isRecord(value.asset)) fail('tileset 缺少 asset 声明')
    const json = value as Record<string, unknown> & { asset: Record<string, unknown> }
    if (json.asset.version !== '1.1') {
      throw new TilesError({
        code: 'unsupported-version',
        stage: 'hierarchy',
        sourceUrl,
        message: `M0 仅支持 3D Tiles 1.1，收到版本 ${String(json.asset.version)}`,
      })
    }

    const checkExtensions = (object: Record<string, unknown>, path: string): void => {
      const used = object.extensionsUsed
      const required = object.extensionsRequired
      for (const [name, entries] of [
        ['extensionsUsed', used],
        ['extensionsRequired', required],
      ] as const) {
        if (
          entries !== undefined &&
          (!Array.isArray(entries) || entries.some((v) => typeof v !== 'string'))
        ) {
          fail(`${name} 必须是扩展名称数组`, path)
        }
      }
      if (Array.isArray(required) && required.length) {
        if (!Array.isArray(used) || required.some((name) => !used.includes(name))) {
          fail('extensionsRequired 必须包含于 extensionsUsed', path)
        }
        fail(`M0 未实现 tileset 必需扩展: ${required[0]}`, path, required[0])
      }
      if (isRecord(object.extensions)) {
        const names = Object.keys(object.extensions)
        // 这些可选字段依然改变必要层次/内容语义，没有 M0 的等价回退。
        for (const name of names) {
          if (['3DTILES_implicit_tiling', '3DTILES_multiple_contents'].includes(name)) {
            fail(`M0 不支持扩展语义: ${name}`, path, name)
          }
          warnings.push(`未解释可选扩展 ${name} (${path})；仅使用核心字段`)
        }
      } else if (object.extensions !== undefined) {
        fail('extensions 必须是对象', path)
      }
    }
    checkExtensions(json, 'root')
    const errorValue = (input: unknown, path: string): number => {
      if (typeof input !== 'number' || !Number.isFinite(input) || input < 0) {
        fail('geometricError 必须是有限非负数', path)
      }
      return input as number
    }
    const metadata = (
      object: Record<string, unknown>,
      scope: MetadataRef['scope']
    ): MetadataRef | undefined => {
      if (object.metadata === undefined) return undefined
      if (!isRecord(object.metadata)) fail('metadata 必须是对象')
      warnings.push(`${scope} 元数据仅保留引用，M0 未启用查询或样式`)
      return Object.freeze({ scope, sourceUrl, value: object.metadata })
    }

    const node = (
      input: unknown,
      path: string,
      inherited?: TileRefinement,
      depth = 0
    ): SpatialNode => {
      if (!isRecord(input)) fail('tile 必须是对象', path)
      const tile = input as Record<string, unknown>
      if (depth > (options.maxDepth ?? 64)) fail('显式层次超出最大嵌套深度', path)
      checkExtensions(tile, path)
      for (const feature of ['implicitTiling', 'viewerRequestVolume'] as const) {
        if (tile[feature] !== undefined) fail(`M0 不支持 ${feature}`, path, feature)
      }
      const refine = tile.refine ?? inherited
      if (refine !== 'ADD' && refine !== 'REPLACE')
        fail('根必须声明有效 refine，子节点可继承', path)
      let transform = IDENTITY
      if (tile.transform !== undefined) {
        if (
          !Array.isArray(tile.transform) ||
          tile.transform.length !== 16 ||
          tile.transform.some((number) => typeof number !== 'number' || !Number.isFinite(number)) ||
          tile.transform[3] !== 0 ||
          tile.transform[7] !== 0 ||
          tile.transform[11] !== 0 ||
          tile.transform[15] !== 1
        ) {
          fail('tile transform 必须是有限的 4×4 仿射矩阵', path)
        }
        transform = Object.freeze([...(tile.transform as number[])])
      }
      let boundingVolume: SpatialNode['boundingVolume']
      try {
        boundingVolume = parseTilesBoundingVolume(tile.boundingVolume)
      } catch (cause) {
        fail(`tile 包围体无效: ${cause instanceof Error ? cause.message : String(cause)}`, path)
      }
      if (tile.content !== undefined && tile.contents !== undefined) {
        fail('content 与 contents 互斥', path)
      }
      let contentJson = tile.content
      if (tile.contents !== undefined) {
        if (!Array.isArray(tile.contents) || tile.contents.length === 0) {
          fail('contents 必须是非空数组', path)
        }
        if ((tile.contents as unknown[]).length !== 1) {
          fail('M0 不支持多内容', path, 'contents')
        }
        contentJson = (tile.contents as unknown[])[0]
      }
      let content: ContentRef | undefined
      const id = `${options.idPrefix ?? sourceUrl}#${path}`
      if (contentJson !== undefined) {
        if (
          !isRecord(contentJson) ||
          typeof contentJson.uri !== 'string' ||
          !contentJson.uri.trim()
        ) {
          fail('content.uri 必须是非空字符串', path)
        }
        const data = contentJson as Record<string, unknown> & { uri: string }
        checkExtensions(data, path)
        if (data.group !== undefined) fail('M0 未实现内容分组语义', path, 'group')
        let contentBounds: ContentRef['boundingVolume']
        if (data.boundingVolume !== undefined) {
          try {
            contentBounds = parseTilesBoundingVolume(data.boundingVolume)
          } catch (cause) {
            fail(
              `content 包围体无效: ${cause instanceof Error ? cause.message : String(cause)}`,
              path
            )
          }
        }
        content = Object.freeze({
          id: `${id}/content/0`,
          uri: data.uri,
          context,
          metadata: metadata(data, 'content'),
          boundingVolume: contentBounds,
        })
      }
      if (tile.children !== undefined && !Array.isArray(tile.children)) {
        fail('children 必须是数组', path)
      }
      const children =
        (tile.children as unknown[] | undefined)?.map((child, index) =>
          node(child, `${path}/children/${index}`, refine as TileRefinement, depth + 1)
        ) ?? []
      return Object.freeze({
        id,
        documentUrl: sourceUrl,
        path,
        transform,
        boundingVolume: boundingVolume!,
        error: Object.freeze({
          value: errorValue(tile.geometricError, path),
          unit: 'source' as const,
        }),
        refine: refine as TileRefinement,
        content,
        children: Object.freeze(children),
        hasDeclaredChildren: tile.children !== undefined,
        metadata: metadata(tile, 'tile'),
      })
    }
    const root = node(json.root, 'root')
    const tilesetMetadata = metadata(json, 'tileset')
    return Object.freeze({
      url: sourceUrl,
      version: '1.1',
      geometricError: errorValue(json.geometricError, 'tileset'),
      root,
      metadata: tilesetMetadata,
      warnings: Object.freeze(warnings),
      byteLength: options.byteLength ?? 0,
    })
  }
}
